/**
 * useInformantManagement
 * ======================
 *
 * Shared hook that encapsulates all informant management logic used by both
 * ProviderManageInformantsForm and PatientManageInformantsForm.
 *
 * This hook owns:
 *   - Loading informant data from Firestore (enriching the patient's
 *     informants map with each informant's own document data such as
 *     email, formData progress, hasSetOwnPassword, etc.)
 *   - Sorting informants (Adulthood first, then Childhood, then alphabetical)
 *   - Computing per-informant progress warnings (has the informant started
 *     or completed any questionnaires?)
 *   - Determining which actions are available based on the current state
 *     (e.g., SWAP is only possible when two informants each have a single
 *     different life stage; SPLIT only when one informant owns both stages)
 *   - Calling the backend `manageInformantAssignment` function for SPLIT,
 *     SWAP, and ADD operations
 *   - Calling existing backend functions (setupNewInformant,
 *     changeInformantEmailAddress, replaceInformant, etc.)
 *   - Managing loading / error / success state for each action
 *
 * The hook does NOT own any UI rendering — it returns data and action
 * handlers that the consumer component can wire up to its own JSX.
 *
 * Usage (simplified):
 * -------------------
 *   const {
 *     informants,
 *     isLoading,
 *     availableActions,
 *     splitInformant,
 *     swapInformants,
 *     addInformant,
 *     ...
 *   } = useInformantManagement({ patientId, patientDataModel, refreshPatientData });
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { doc, getDoc, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { sanitizeNameInput, sanitizeEmailInput } from '../utils/inputSanitization';
import {
  computeModularInformantProgress,
  getModularInformantFormInstancesCollectionRef,
  getInformantProfileDocRef,
  isAutismInformantEntry,
  isAutismPatient,
} from '../utils/informantFirestorePaths';
import { getInformantFormKeys } from '../constants/informantFormKeys';

// ────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────

/**
 * Map Firebase error codes to user-facing error messages.
 * These are consistent across provider and patient views.
 */
const ERROR_MESSAGES = {
  'functions/already-exists': 'An account with this email address already exists.',
  'functions/permission-denied': 'You do not have permission to perform this action.',
  'functions/invalid-argument': 'One or more fields are invalid. Please check and try again.',
  'functions/unauthenticated': 'Your session has expired. Please sign in again.',
  'functions/failed-precondition': 'This action cannot be performed right now.',
  'functions/not-found': 'The requested person could not be found.',
};

/**
 * Resolve a user-facing error message from a Firebase callable error.
 *
 * Checks the error's `code` against known Firebase error codes first,
 * falling back to the raw `message` property or a generic fallback.
 */
function resolveErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
  if (!error) return fallback;
  const code = error?.code || '';
  return ERROR_MESSAGES[code] || error?.message || fallback;
}

// ────────────────────────────────────────────────────────────────────
// Informant data helpers
// ────────────────────────────────────────────────────────────────────

/**
 * Sort informants by life stage priority (Adulthood > Childhood) then by name.
 *
 * Returns a new array — never mutates the input.
 */
function sortInformants(informants) {
  const stagePriority = { Adulthood: 0, Childhood: 1 };
  return [...informants].sort((a, b) => {
    const stageA = Math.min(...(a.lifeStages || []).map(s => stagePriority[s] ?? 2));
    const stageB = Math.min(...(b.lifeStages || []).map(s => stagePriority[s] ?? 2));
    if (stageA !== stageB) return stageA - stageB;
    return (a.name || '').localeCompare(b.name || '');
  });
}

/**
 * Known informant form keys per life stage, ordered by lookup priority.
 * When a new informant instrument is added to the system (via
 * instrumentSettingsConfig.js + updateOrgConfig), add its key here to ensure
 * the frontend can resolve progress correctly without a fallback scan.
 */
const KNOWN_FORM_KEYS = {
  Adulthood: ['asrsOAdulthoodInformant', 'baarsIVCurrentInformant'],
  Childhood: ['snapIVChildhoodInformant', 'baarsIVChildhoodInformant'],
};

/**
 * Patterns used as a safety-net to discover informant form keys that aren't in
 * KNOWN_FORM_KEYS (e.g. a newly added instrument).  We look for keys ending in
 * "Informant" that contain a stage hint: "Adulthood" or "Current" for adulthood
 * instruments, "Childhood" for childhood instruments.
 */
const STAGE_KEY_PATTERNS = {
  Adulthood: /(?:Adulthood|Current).*Informant$/,
  Childhood: /Childhood.*Informant$/,
};

/**
 * Resolve the correct Firestore form key for a given life stage within an
 * informant's formData.  Different organisations may use different instruments
 * (e.g. ASRS-O vs BAARS-IV for Adulthood, SNAP-IV vs BAARS-IV for Childhood),
 * so we check which key is actually present rather than hard-coding one.
 *
 * Resolution strategy:
 *   1. Check KNOWN_FORM_KEYS for the stage — fast path that covers all
 *      instruments currently in the system.
 *   2. If none matched, scan all keys in formData for any that match the
 *      stage's regex pattern — catches future instruments added via
 *      instrumentSettingsConfig without a code change.
 *   3. Fall back to the default key for the stage (ensures we always return
 *      something, even if formData is null/empty).
 *
 * @param {string} lifeStage  – 'Adulthood' or 'Childhood'
 * @param {Object} formDataObj – The informant's formData from Firestore
 * @returns {string} The form key to use for progress/data lookups
 */
export function resolveInformantFormKey(lifeStage, formDataObj) {
  const knownCandidates = KNOWN_FORM_KEYS[lifeStage] || KNOWN_FORM_KEYS.Adulthood;

  for (const key of knownCandidates) {
    if (formDataObj && formDataObj[key]) return key;
  }

  // Safety-net: scan formData for any key matching the stage's pattern.
  // This handles instruments added after this code was last updated.
  if (formDataObj) {
    const pattern = STAGE_KEY_PATTERNS[lifeStage];
    if (pattern) {
      const discoveredKey = Object.keys(formDataObj).find(k => pattern.test(k));
      if (discoveredKey) return discoveredKey;
    }
  }

  return knownCandidates[knownCandidates.length - 1];
}

/**
 * Compute a human-readable status for an informant based on their form progress
 * and account state.  Used in the detail screen to show an at-a-glance status badge.
 *
 * Status hierarchy (highest to lowest priority):
 *   1. "Completed all questionnaires" (all forms at 100%)
 *   2. "Started answering" (any form > 0%)
 *   3. "Logged in" (set their own password but haven't started forms)
 *   4. "Invited" (account created but haven't logged in yet)
 *
 * @param {Object} informant – Enriched informant object from loadInformants
 * @returns {{ label: string, color: string, icon: string }}
 */
export function getInformantStatus(informant) {
  if (informant.hasCompleted) {
    return {
      label: 'Completed all questionnaires',
      color: 'text-green-700',
      icon: 'completed',
    };
  }
  if (informant.hasStarted) {
    return {
      label: 'Started answering questionnaires',
      color: 'text-blue-600',
      icon: 'progress',
    };
  }
  if (informant.hasSetOwnPassword) {
    return {
      label: 'Logged in',
      color: 'text-purple-600',
      icon: 'logged-in',
    };
  }
  return {
    label: 'Invited — awaiting login',
    color: 'text-gray-500',
    icon: 'invited',
  };
}

/**
 * Build per-life-stage progress info for an informant.  Returns an object
 * keyed by life stage ('Adulthood' / 'Childhood') with the form key, progress
 * percentage, and timestamps.  This powers the per-questionnaire progress
 * bars on the detail screen.
 *
 * @param {string[]} lifeStages – The informant's assigned life stages
 * @param {Object}   formData   – The informant's formData from Firestore
 * @returns {Object} e.g. { Adulthood: { formKey, percentage, completedAt, lastUpdated } }
 */
function buildFormProgress(lifeStages, formData) {
  const progress = {};
  for (const stage of (lifeStages || [])) {
    const formKey = resolveInformantFormKey(stage, formData);
    const form = formData?.[formKey];
    progress[stage] = {
      formKey,
      percentage: form?.progress?.progressPercentage || 0,
      completedAt: form?.progress?.completedAt || null,
      lastUpdated: form?.progress?.lastUpdated || null,
    };
  }
  return progress;
}

/**
 * Determine whether an informant has started any of their assigned
 * questionnaires (progressPercentage > 0 on any form).
 */
function hasStartedQuestionnaires(formData) {
  if (!formData || typeof formData !== 'object') return false;
  return Object.values(formData).some(
    form => typeof form === 'object' && form?.progress?.progressPercentage > 0
  );
}

/**
 * Determine whether an informant has completed ALL assigned questionnaires.
 * Checks that every assigned life stage has a corresponding form in formData
 * with progressPercentage === 100.  This prevents a false "completed" status
 * when a second questionnaire is newly assigned but hasn't been started yet
 * (and therefore has no formData entry).
 */
function hasCompletedAllQuestionnaires(formData, lifeStages) {
  if (!formData || typeof formData !== 'object') return false;
  if (!lifeStages || lifeStages.length === 0) return false;

  for (const stage of lifeStages) {
    const formKey = resolveInformantFormKey(stage, formData);
    const form = formData[formKey];
    if (!form?.progress || form.progress.progressPercentage !== 100) {
      return false;
    }
  }
  return true;
}

// ────────────────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────────────────

/**
 * @param {Object} options
 * @param {string} options.patientId            – UID of the patient
 * @param {Object} options.patientDataModel     – The full patient document (must include `informants` map)
 * @param {Function} options.refreshPatientData – Callback to reload patient data after mutations
 * @param {string}  [options.callerType='patient'] – 'patient' or 'provider'
 * @param {Object}  [options.terminology]         – Dynamic label overrides (e.g. "Key Contact" for SLaM)
 */
export default function useInformantManagement({
  patientId,
  patientDataModel,
  refreshPatientData,
  callerType = 'patient',
  terminology = { singular: 'Informant', lowerSingular: 'informant' },
}) {
  // ── Data state ──
  const [informants, setInformants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // ── Per-action mutation state ──
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);

  // Track mounted state to prevent setState after unmount (e.g. when the
  // modal is closed while a Firestore read or Cloud Function call is
  // still in flight).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Derive stable references outside the callback so the dependency array
  // exactly matches what the callback reads — no eslint suppression needed.
  const hasPatientData = !!patientDataModel;
  const informantsMap = patientDataModel?.informants ?? null;
  // Pathway dispatch: autism and CYP ADHD are distinct modular pathways with
  // different informant models (autism: up to 2 peers; CYP: exactly one
  // guardian + one school rep, no life stages). Deriving only isAutismPathway
  // here used to send CYP patients down the adult-ADHD life-stage logic.
  const patientModule = patientDataModel?.assessmentModule || 'adultAdhd';
  const isAutismPathway = isAutismPatient(patientDataModel);
  const isCypPathway = patientModule === 'cypAdhd';

  // ── Load & enrich informant data ──
  const loadInformants = useCallback(async () => {
    if (!hasPatientData) {
      if (mountedRef.current) {
        setInformants(prev => prev.length > 0 ? prev : []);
        setIsLoading(false);
        setLoadError(prev => prev || 'Patient data is not available. Please close and reopen this dialog.');
      }
      return;
    }

    if (!informantsMap) {
      if (mountedRef.current) {
        // Only clear informants on the initial load.  During a refresh
        // (e.g. onSnapshot firing after a batch write), informantsMap may
        // be transiently null/undefined.  Clearing the array would cause
        // the parent's loading guard to activate, unmounting any active
        // action flow subcomponent and destroying its internal state.
        setInformants(prev => prev.length > 0 ? prev : []);
        setIsLoading(false);
        setLoadError(null);
      }
      return;
    }

    try {
      if (mountedRef.current) {
        setIsLoading(true);
        setLoadError(null);
      }
      const entries = Object.entries(informantsMap);

      // Fetch each informant's own Firestore document in parallel to
      // get live email, form progress, and password-set status.
      const enriched = await Promise.all(
        entries.map(async ([id, refData]) => {
          const isAutismInformant = isAutismInformantEntry(refData);
          const base = {
            id,
            name: refData.informantName || 'Unknown',
            firstName: (refData.informantName || '').split(' ')[0] || '',
            lastName: (refData.informantName || '').split(' ').slice(1).join(' ') || '',
            email: refData.informantEmail || '',
            relationship: refData.informantRelationshipToPatient || '',
            lifeStages: isAutismInformant ? [] : (refData.assignedForLifeStages || []),
            isAutismInformant,
            // CYP ADHD role/school metadata — drives role-aware labels and
            // the guardian/schoolRep slot model in availableActions below.
            informantRole: refData.informantRole || null,
            schoolName: refData.schoolName || null,
          };

          try {
            const profileSnap = await getDoc(getInformantProfileDocRef(id, refData));
            if (!profileSnap.exists()) {
              return { ...base, formData: {}, hasSetOwnPassword: false, hasStarted: false, hasCompleted: false, formProgress: {}, createdAt: null };
            }

            const profile = profileSnap.data();

            if (isAutismInformant) {
              // Modular informant (autism or CYP): read the pathway's own
              // formInstances subtree and measure progress over the (module,
              // role) form list — a CYP school rep's 4 forms must not be
              // averaged over autism's 7 keys.
              const entryModule = refData?.assessmentModule || 'adultAutism';
              const formsSnap = await getDocs(
                getModularInformantFormInstancesCollectionRef(id, patientId, entryModule),
              );
              const formInstances = {};
              formsSnap.forEach((d) => {
                formInstances[d.id] = d.data();
              });
              const informantRole = refData?.informantRole || profile?.informantRole;
              const progress = computeModularInformantProgress(
                formInstances,
                getInformantFormKeys(entryModule, informantRole),
              );

              // CYP ADHD per-informant lock state: the patient header stores
              // formsUnlocked on the per-informant entry when a clinician
              // unlocks that respondent. If present (and the pipeline has
              // synced), it means forms are unlocked despite a report having
              // been generated. We expose this so the Manage dialog can
              // show lock status and offer the toggle.
              const formsUnlocked = refData?.formsUnlocked === true;
              const formsUnlockedByName = refData?.formsUnlockedByName || null;

              return {
                ...base,
                email: profile.email || base.email,
                formData: {},
                formInstances,
                hasSetOwnPassword: profile.hasSetOwnPassword ?? false,
                hasAuthAccount: profile.hasAuthAccount ?? true,
                isMFASetUp: profile.isMFASetUp ?? false,
                mfaMethod: profile.mfaMethod ?? null,
                hasStarted: progress.hasStarted,
                hasCompleted: progress.hasCompleted,
                formProgress: progress.formProgress,
                firstName: profile.firstName || '',
                lastName: profile.lastName || '',
                organisation: profile.organisation || '',
                createdAt: profile.createdAt || null,
                formsUnlocked,
                formsUnlockedByName,
                // CYP ADHD: the guardian profile may hold a schoolRepNomination
                // with { status: 'skipped', reason, reasonCode } when the
                // parent/carer explicitly skipped nominating a school rep.
                // We surface this in the Manage dialog so clinicians can see
                // the skip reason alongside the informant list.
                schoolRepNomination: profile.schoolRepNomination || null,
              };
            }

            const fd = profile.formData || {};
            return {
              ...base,
              email: profile.email || base.email,
              formData: fd,
              hasSetOwnPassword: profile.hasSetOwnPassword ?? false,
              hasAuthAccount: profile.hasAuthAccount ?? true,
              isMFASetUp: profile.isMFASetUp ?? false,
              mfaMethod: profile.mfaMethod ?? null,
              hasStarted: hasStartedQuestionnaires(fd),
              hasCompleted: hasCompletedAllQuestionnaires(fd, base.lifeStages),
              formProgress: buildFormProgress(base.lifeStages, fd),
              firstName: profile.firstName || '',
              lastName: profile.lastName || '',
              organisation: profile.organisation || '',
              createdAt: profile.createdAt || null,
            };
          } catch (e) {
            console.warn(`Failed to enrich informant ${id}:`, e);
          }

          return { ...base, formData: {}, hasSetOwnPassword: false, hasStarted: false, hasCompleted: false, formProgress: {}, createdAt: null };
        })
      );

      if (!mountedRef.current) return;

      // De-duplicate by informant ID (shouldn't happen, but defensive)
      const uniqueMap = new Map();
      enriched.forEach(inf => {
        if (!uniqueMap.has(inf.id)) uniqueMap.set(inf.id, inf);
      });

      setInformants(sortInformants([...uniqueMap.values()]));
    } catch (err) {
      console.error('Error loading informants:', err);
      if (mountedRef.current) {
        setLoadError('Failed to load data. Please close and reopen this dialog.');
      }
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [hasPatientData, informantsMap, patientId]);

  // Clear stale data immediately when patientId changes (e.g. provider
  // navigates from one patient's modal to another's).
  useEffect(() => {
    setInformants([]);
    setLoadError(null);
    setIsLoading(true);
  }, [patientId]);

  useEffect(() => {
    loadInformants();
  }, [loadInformants]);

  // ── Derived: available actions ──
  const availableActions = useMemo(() => {
    const actions = {
      canSplit: false,
      canSwap: false,
      canAdd: false,
      splitInformantId: null,
      swapInformantIds: null,
      addLifeStage: null,
    };

    // Guard: treat missing / undefined lifeStages as empty array
    const safeStages = (inf) => inf.lifeStages || [];

    // CYP ADHD: exactly one guardian (created at patient setup) + one school
    // representative (nominated). No life stages, no split/swap. The only
    // add action is filling whichever role slot is empty.
    if (isCypPathway) {
      const hasGuardian = informants.some((inf) => inf.informantRole === 'legalGuardian');
      const hasSchoolRep = informants.some((inf) => inf.informantRole === 'schoolRep');
      if (!hasGuardian) {
        actions.canAdd = true;
        actions.addRole = 'legalGuardian';
      } else if (!hasSchoolRep) {
        actions.canAdd = true;
        actions.addRole = 'schoolRep';
      }
      return actions;
    }

    // Adult Autism: up to 2 informants, no life-stage split/swap/split actions.
    if (isAutismPathway) {
      if (informants.length < 2) {
        actions.canAdd = true;
        actions.addLifeStage = null;
      }
      return actions;
    }

    if (informants.length > 0) {
      // SPLIT: exactly one informant with both life stages
      const bothStagesInformant = informants.find(
        inf => safeStages(inf).length === 2
          && safeStages(inf).includes('Adulthood')
          && safeStages(inf).includes('Childhood')
      );
      if (bothStagesInformant) {
        actions.canSplit = true;
        actions.splitInformantId = bothStagesInformant.id;
      }

      // SWAP: exactly two informants, each with a single (different) life stage
      if (informants.length === 2) {
        const [a, b] = informants;
        if (
          safeStages(a).length === 1 && safeStages(b).length === 1
          && safeStages(a)[0] !== safeStages(b)[0]
        ) {
          actions.canSwap = true;
          actions.swapInformantIds = [a.id, b.id];
        }
      }
    }

    // ADD: a life stage has no informant assigned to it.
    // This applies even when there are 0 informants — both stages are uncovered.
    const coveredStages = new Set(informants.flatMap(inf => safeStages(inf)));
    if (!coveredStages.has('Adulthood')) {
      actions.canAdd = true;
      actions.addLifeStage = 'Adulthood';
    } else if (!coveredStages.has('Childhood')) {
      actions.canAdd = true;
      actions.addLifeStage = 'Childhood';
    }

    return actions;
  }, [informants, isAutismPathway, isCypPathway]);

  // ── Progress-based warnings ──
  /**
   * Build a human-readable warning message for an informant based on their
   * questionnaire progress.  Returns null if no warning is needed.
   */
  const getProgressWarning = useCallback((informantId) => {
    const inf = informants.find(i => i.id === informantId);
    if (!inf) return null;

    const label = terminology.lowerSingular || 'informant';
    if (inf.hasCompleted) {
      return `This ${label} has completed all their questionnaires. Changing their assignment will archive their answers.`;
    }
    if (inf.hasStarted) {
      return `This ${label} has started answering their questionnaires. Changing their assignment will archive their progress.`;
    }
    return null;
  }, [informants, terminology.lowerSingular]);

  // ── Action helpers ──
  const clearActionState = useCallback(() => {
    setActionError(null);
    setActionSuccess(null);
  }, []);

  /**
   * Generic wrapper for calling a backend Cloud Function.
   *
   * Handles loading state, error resolution, and success callbacks
   * so that individual action methods don't duplicate this boilerplate.
   */
  // Guard against overlapping actions — only one mutation may run at a
  // time.  Without this, rapid clicks could trigger parallel Cloud
  // Function calls that operate on the same informant documents.
  const actionLockRef = useRef(false);

  const callFunction = useCallback(async (functionName, payload, successMessage) => {
    if (actionLockRef.current) {
      console.warn('Action already in progress — ignoring duplicate call');
      return null;
    }

    actionLockRef.current = true;
    if (mountedRef.current) {
      setActionLoading(true);
      setActionError(null);
      setActionSuccess(null);
    }

    try {
      const fn = httpsCallable(functions, functionName);
      const result = await fn(payload);
      if (mountedRef.current) {
        setActionSuccess(successMessage || 'Action completed successfully.');
      }
      return result.data;
    } catch (err) {
      const msg = resolveErrorMessage(err);
      if (mountedRef.current) setActionError(msg);
      throw err;
    } finally {
      actionLockRef.current = false;
      if (mountedRef.current) setActionLoading(false);
    }
  }, []);

  // ── SPLIT ──
  /**
   * Split one informant who currently has both life stages into two:
   * the existing informant keeps one stage, a new informant is created
   * for the other.
   *
   * @param {string} existingInformantId – The informant currently owning both stages
   * @param {string} lifeStageForNew     – Which stage to assign to the new informant
   * @param {Object} newInformantDetails  – { firstName, lastName, email, relationship }
   */
  const splitInformant = useCallback(async (existingInformantId, lifeStageForNew, newInformantDetails) => {
    const result = await callFunction('manageInformantAssignment', {
      patientId,
      operation: 'SPLIT',
      existingInformantId,
      lifeStageForNewInformant: lifeStageForNew,
      newInformant: newInformantDetails,
    }, 'Added successfully. A welcome email has been sent.');

    // Refresh deferred to the confirmation screen's "Done" handler.
    return result;
  }, [patientId, callFunction]);

  // ── SWAP ──
  /**
   * Swap the questionnaire assignments between two existing informants.
   * Both informants must each have exactly one (different) life stage.
   * Their existing answers will be archived and they start fresh.
   *
   * @param {string} informantAId – First informant
   * @param {string} informantBId – Second informant
   */
  const swapInformants = useCallback(async (informantAId, informantBId) => {
    const result = await callFunction('manageInformantAssignment', {
      patientId,
      operation: 'SWAP',
      informantAId,
      informantBId,
    }, 'Questionnaires switched successfully.');

    // Refresh deferred to the confirmation screen's "Done" handler.
    return result;
  }, [patientId, callFunction]);

  // ── ADD ──
  /**
   * Add a brand-new informant for a life stage that has no current informant.
   *
   * @param {string} lifeStage           – 'Adulthood' or 'Childhood'
   * @param {Object} newInformantDetails  – { firstName, lastName, email, relationship }
   */
  const addInformantForStage = useCallback(async (lifeStage, newInformantDetails) => {
    const result = await callFunction('manageInformantAssignment', {
      patientId,
      operation: 'ADD',
      lifeStage,
      newInformant: newInformantDetails,
    }, 'Added successfully. A welcome email has been sent.');

    // Refresh deferred to the confirmation screen's "Done" handler.
    return result;
  }, [patientId, callFunction]);

  // ── Existing actions (delegates to legacy callable functions) ──

  /**
   * Set up a brand-new informant via the existing setupNewInformant endpoint.
   * This is used by the original "Add Informant" flow.
   */
  const setupNewInformant = useCallback(async (informantDetails) => {
    const payload = {
      patientId,
      ...informantDetails,
    };
    if (isAutismPathway) {
      payload.assessmentModule = 'adultAutism';
      delete payload.informantForLifeStage;
    }
    if (isCypPathway) {
      // CYP ADHD informants are role-based (legalGuardian / schoolRep), not
      // life-staged. The caller supplies informantRole (and schoolName for
      // school representatives) in informantDetails.
      payload.assessmentModule = 'cypAdhd';
      delete payload.informantForLifeStage;
    }

    const result = await callFunction('setupNewInformant', payload, 'Set up successfully. A welcome email has been sent.');

    // Refresh deferred to the confirmation screen's "Done" handler.
    return result;
  }, [patientId, callFunction, isAutismPathway, isCypPathway]);

  /**
   * Change an informant's email address.
   */
  const changeEmail = useCallback(async (informantId, newEmail) => {
    const result = await callFunction('changeInformantEmailAddress', {
      informantId,
      patientId,
      newEmail,
    }, 'Email address changed successfully. A new welcome email has been sent.');

    // Refresh is intentionally deferred — the backend creates a new Auth
    // account (new UID), so refreshing here would cause the selectedInformant
    // sync effect to navigate away before the success screen can render.
    // The confirmation screen's "Done" handler calls refreshInformants.
    return result;
  }, [patientId, callFunction]);

  /**
   * Update an informant's relationship to the patient. This is a simple
   * metadata update — no account changes, no emails sent. The backend
   * writes to both the informant's doc and the patient's informant map
   * atomically.
   */
  const changeRelationship = useCallback(async (informantId, newRelationship) => {
    const result = await callFunction('changeInformantRelationship', {
      informantId,
      patientId,
      newRelationship,
    }, 'Relationship updated successfully.');
    return result;
  }, [patientId, callFunction]);

  /**
   * Replace an existing informant with a new person.
   */
  const replaceInformant = useCallback(async (oldInformantId, newInformantDetails) => {
    const result = await callFunction('replaceInformant', {
      patientId,
      oldInformantId,
      ...newInformantDetails,
    }, 'Replaced successfully. A welcome email has been sent.');

    // Refresh deferred — replacing creates a new informant document (new UID),
    // so refreshing here would cause the sync effect to clear selectedInformant
    // before the success screen renders. The "Done" handler triggers refresh.
    return result;
  }, [patientId, callFunction]);

  /**
   * Resend the welcome email to an informant (Provider only).
   */
  const resendWelcomeEmail = useCallback(async (informantId) => {
    const result = await callFunction('resendWelcomeEmail', {
      targetUserId: informantId,
      patientId,
    }, 'Welcome email resent successfully.');
    return result;
  }, [patientId, callFunction]);

  /**
   * Reset (remove all) MFA factors for an informant.
   *
   * The Cloud Function needs to know which Firestore collection holds the
   * informant's profile doc so it can sync MFA metadata.  Autism / CYP
   * modular informants live in `informants/{uid}`, ADHD informants live in
   * `patients/{uid}`.  We look up the informant in the enriched list to
   * determine the correct targetUserType.
   */
  const resetInformantMfa = useCallback(async (informantId) => {
    const informant = informants.find(i => i.id === informantId);
    const targetUserType = informant?.isAutismInformant ? 'informant' : 'patient';
    const result = await callFunction('resetUserMfa', {
      targetUserId: informantId,
      targetUserType,
    }, 'Two-factor authentication has been reset.');
    return result;
  }, [informants, callFunction]);

  /**
   * Deactivate an informant.
   */
  const deactivateInformant = useCallback(async (informantId, reason) => {
    const result = await callFunction('deactivateInformantEndpoint', {
      informantId,
      patientId,
      reason,
    }, 'Removed successfully.');

    // Refresh deferred — deactivation removes the informant document, so
    // refreshing here would cause the sync effect to clear selectedInformant
    // before the success screen renders. The "Done" handler triggers refresh.
    return result;
  }, [patientId, callFunction]);

  /**
   * Reassign an informant's questionnaires (add a life stage).
   * This uses the legacy reassignInformantQuestionnaires endpoint.
   */
  const reassignQuestionnaires = useCallback(async (informantId, lifeStage) => {
    const result = await callFunction('reassignInformantQuestionnaires', {
      informantId,
      patientId,
      operation: 'add_life_stage',
      lifeStage,
    }, 'Questionnaire assignment updated successfully.');

    // Refresh deferred to the confirmation screen's "Done" handler for
    // consistency with other actions.
    return result;
  }, [patientId, callFunction]);

  // ── CYP ADHD per-informant form lock toggle ──
  /**
   * Unlock or re-lock a CYP ADHD informant's questionnaire forms.
   *
   * Calls the existing `unlockPatientForms` Cloud Function, which now has a
   * CYP branch expecting `informantId` and `unlock` (boolean) alongside
   * `patientId`. The backend writes `informants.{id}.formsUnlocked` on the
   * patient header and triggers the denorm sync onto the informant profile.
   *
   * @param {string}  informantId – UID of the informant to toggle
   * @param {boolean} unlock      – true to unlock, false to re-lock
   */
  const toggleInformantFormLock = useCallback(async (informantId, unlock) => {
    const label = unlock ? 'unlocked' : 're-locked';
    const result = await callFunction('unlockPatientForms', {
      patientId,
      informantId,
      unlock,
    }, `Questionnaire forms ${label} successfully.`);
    return result;
  }, [patientId, callFunction]);

  return {
    // Data
    informants,
    isLoading,
    loadError,

    // Derived state
    availableActions,
    isAutismPathway,
    isCypPathway,

    // Mutation state
    actionLoading,
    actionError,
    actionSuccess,
    clearActionState,

    // Helpers
    getProgressWarning,

    // Actions (new unified operations)
    splitInformant,
    swapInformants,
    addInformantForStage,

    // Actions (legacy operations)
    setupNewInformant,
    changeEmail,
    changeRelationship,
    replaceInformant,
    resendWelcomeEmail,
    resetInformantMfa,
    deactivateInformant,
    reassignQuestionnaires,
    toggleInformantFormLock,

    // Utilities exposed for consumer validation
    sanitizeNameInput,
    sanitizeEmailInput,

    // Data refresh
    refreshInformants: loadInformants,
  };
}
